import React, { createContext, ReactNode, useContext, useEffect, useMemo } from 'react';
import { SoarStoreProvider, useSoarStore } from './SoarStoreProvider';

/**
 * Sync component to bridge React context with the MobX store.
 * This is necessary for non-React code like axios interceptors.
 * Refactored to use the new hook-based store.
 */
const SoarStoreSync = () => {
  const { accessToken, accountId, onRefresh } = useSoarContext();
  const { auth } = useSoarStore();

  useEffect(() => {
    auth.setAuth({ accessToken, accountId, onRefresh });
  }, [accessToken, accountId, onRefresh, auth.setAuth]);

  return null;
};

/**
 * Portable user interface that can be extracted to an external library.
 */
export interface SoarUser {
  userId: string;
  userName: string;
  email: string;
  permissions: any; // Raw permissions data
  featureFlags: Record<string, boolean>;
  [key: string]: any;
}

/**
 * The state provided by the SoarContext.
 */
export interface SoarContextState {
  accountId: string;
  accessToken?: string; // Needed for API calls from the external app
  user: SoarUser | null;
  /**
   * Callback to trigger a token refresh in the host application.
   * Returns a promise that resolves when the refresh is complete.
   */
  onRefresh: () => Promise<void | string>;
}

const SoarContext = createContext<SoarContextState | undefined>(undefined);

/**
 * Core SoarProvider designed for reusability.
 */
export const SoarProvider = ({
  children,
  accountId,
  accessToken,
  user,
  onRefresh,
}: {
  children: ReactNode;
  accountId: string;
  accessToken?: string;
  user: SoarUser | null;
  onRefresh: () => Promise<void | string>;
}) => {
  const value = useMemo(() => ({ accountId, accessToken, user, onRefresh }), [accountId, accessToken, user, onRefresh]);
  return (
    <SoarContext.Provider value={value}>
      <SoarStoreProvider>
        <SoarStoreSync />
        {children}
      </SoarStoreProvider>
    </SoarContext.Provider>
  );
};

/**
 * Custom hook to access soar context data.
 */
export const useSoarContext = () => {
  const context = useContext(SoarContext);
  if (context === undefined) {
    throw new Error('useSoarContext must be used within a SoarProvider');
  }
  return context;
};
